import { useState, useCallback, useRef, useMemo, useEffect, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { Search, Link, ExternalLink, Check } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { SELECTION_COLUMN_DEF, emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ExportButton from '../../components/ui/ExportButton';

ModuleRegistry.registerModules([AllCommunityModule]);

interface ListingRow {
  isg_sku?: string | null;
  title?: string | null;
  /** listing_links.marketplace — TEXT NOT NULL, its OWN stored subfamily
   *  (outside the public.marketplace enum; does NOT die at the enum's death —
   *  slice-B census carries it separately). Channel-grain values. */
  marketplace?: string | null;
  status?: string | null;
  url?: string | null;
  is_primary?: boolean | null;
  listed_at?: string | null;
  last_verified_at?: string | null;
}

interface StatusOrChannelConfig {
  label: string;
  cls: string;
}

type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ── Config ──────────────────────────────────────────────────────────────────

const CHANNEL_CONFIG: Record<string, StatusOrChannelConfig> = {
  amazon_fba: { label: 'Amazon FBA', cls: 'bg-warning/15 text-warning' },
  amazon_fbm: { label: 'Amazon FBM', cls: 'bg-info/15 text-info' },
  ebay: { label: 'eBay', cls: 'bg-accent/15 text-accent' },
  walmart_self: { label: 'Walmart', cls: 'bg-success/15 text-success' },
  walmart_wfs: { label: 'Walmart WFS', cls: 'bg-success/15 text-success' },
  shopify: { label: 'Shopify', cls: 'bg-info/15 text-info' },
};

const STATUS_CONFIG: Record<string, StatusOrChannelConfig> = {
  active: { label: 'Active', cls: 'bg-success/15 text-success' },
  pending: { label: 'Pending', cls: 'bg-warning/15 text-warning' },
  suppressed: { label: 'Suppressed', cls: 'bg-danger/15 text-danger' },
  inactive: { label: 'Inactive', cls: 'bg-surface-3 text-ink-muted' },
  removed: { label: 'Removed', cls: 'bg-danger/15 text-danger' },
  draft: { label: 'Draft', cls: 'bg-surface-3 text-ink-secondary' },
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'suppressed', label: 'Suppressed' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'removed', label: 'Removed' },
];

// 5-tab + 'all' shape; walmart_wfs intentionally absent — Walmart Marketplace
// listings are one entity for both walmart_self + walmart_wfs (different
// fulfillment, same listing). Day 60b filter preserves this asymmetry: tabs
// are filtered through activePathKeys, so deactivating walmart_self drops the
// 'WMT' tab; deactivating walmart_wfs has no UI effect (already absent).
const CHANNEL_TABS_FALLBACK = [
  { key: 'all', label: 'All' },
  { key: 'amazon_fba', label: 'FBA' },
  { key: 'amazon_fbm', label: 'FBM' },
  { key: 'ebay', label: 'eBay' },
  { key: 'walmart_self', label: 'WMT' },
  { key: 'shopify', label: 'Shopify' },
];

// ── Listings Page ───────────────────────────────────────────────────────────

export default function Listings(): JSX.Element {
  const navigate = useNavigate();
  const { activePathKeys } = useSettingsConfig();
  const gridRef = useRef<AgGridReact<ListingRow> | null>(null);
  const gridApiRef = useRef<GridApi<ListingRow> | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [totalCount, setTotalCount] = useState<number>(0);

  const channelTabs = useMemo(() => {
    if (!activePathKeys || activePathKeys.length === 0) return CHANNEL_TABS_FALLBACK;
    return CHANNEL_TABS_FALLBACK.filter((t) => t.key === 'all' || activePathKeys.includes(t.key));
  }, [activePathKeys]);

  const stateRef = useRef<{
    searchText: string;
    statusFilter: string;
    channelFilter: string;
  }>({ searchText, statusFilter, channelFilter });
  stateRef.current = { searchText, statusFilter, channelFilter };

  const getRowsFn = useCallback(async (params: IGetRowsParams): Promise<void> => {
    const { startRow, endRow, successCallback, failCallback } = params;
    const { searchText: st, statusFilter: sf, channelFilter: cf } = stateRef.current;

    try {
      const { data, error } = await callRpc('get_listings_queue', {
        p_status: sf === 'all' ? null : sf,
        p_channel: cf === 'all' ? null : cf,
        p_search: st.trim() || null,
        p_limit: endRow - startRow,
        p_offset: startRow,
      });
      if (error) throw new Error(error.message);

      const rows = (data as ListingRow[]) || [];
      if (startRow === 0) setTotalCount(rows.length);
      successCallback(
        rows,
        startRow === 0
          ? rows.length < endRow - startRow
            ? rows.length
            : rows.length + 100
          : rows.length
      );
    } catch (e) {
      reportDatasourceFailure(
        'Listings',
        { rpcName: 'listing_links', params: { startRow, endRow } },
        e,
        () => failCallback()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent<ListingRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.setGridOption('datasource', { getRows: getRowsFn });
    }
  }, [searchText, statusFilter, channelFilter, getRowsFn]);

  // ── Cell renderers ──────────────────────────────────────────

  const SkuCell = useCallback(
    (p: ICellRendererParams<ListingRow>): JSX.Element | null => {
      if (!p.data) return null;
      const sku = p.data.isg_sku;
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (sku) navigate(`/catalog/products/${sku}`);
          }}
          className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
        >
          {sku}
        </button>
      );
    },
    [navigate]
  );

  const ChannelCell = useCallback((p: ICellRendererParams<ListingRow>): JSX.Element | null => {
    // listing_links.marketplace read (TEXT subfamily — see ListingRow).
    const mp = p.data?.marketplace;
    if (!mp) return null;
    const cfg = CHANNEL_CONFIG[mp] || { label: mp, cls: 'bg-surface-3 text-ink-muted' };
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', cfg.cls)}>
        {cfg.label}
      </span>
    );
  }, []);

  const StatusCell = useCallback((p: ICellRendererParams<ListingRow>): JSX.Element | null => {
    const st = p.data?.status;
    if (!st) return null;
    const cfg = STATUS_CONFIG[st] || { label: st, cls: 'bg-surface-3 text-ink-muted' };
    return (
      <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', cfg.cls)}>
        {cfg.label}
      </span>
    );
  }, []);

  const UrlCell = useCallback((p: ICellRendererParams<ListingRow>): JSX.Element | null => {
    const url = p.data?.url;
    if (!url) return null;
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="text-xs text-accent hover:text-accent-hover truncate transition-colors cursor-pointer flex items-center gap-1"
      >
        <ExternalLink size={10} className="flex-shrink-0" />
        <span className="truncate">{url.replace(/^https?:\/\/(www\.)?/, '')}</span>
      </a>
    );
  }, []);

  const PrimaryCell = useCallback((p: ICellRendererParams<ListingRow>): JSX.Element | null => {
    if (!p.data) return null;
    return p.data.is_primary ? (
      <Check size={14} className="text-accent" />
    ) : (
      <span className="text-ink-muted">{'\u2014'}</span>
    );
  }, []);

  // ── Column defs ─────────────────────────────────────────────

  const columnDefs = useMemo<ColDef<ListingRow>[]>(
    () =>
      [
        {
          headerName: 'ISG SKU',
          field: 'isg_sku',
          width: 120,
          pinned: 'left',
          suppressMovable: true,
          resizable: false,
          cellRenderer: SkuCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Title',
          field: 'title',
          flex: 1,
          minWidth: 180,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Channel',
          // listing_links.marketplace column key (TEXT subfamily — see ListingRow).
          field: 'marketplace',
          width: 120,
          cellRenderer: ChannelCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 110,
          cellRenderer: StatusCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'URL',
          field: 'url',
          width: 200,
          cellRenderer: UrlCell,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'Primary',
          field: 'is_primary',
          width: 75,
          cellRenderer: PrimaryCell,
          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        },
        {
          headerName: 'Listed',
          field: 'listed_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<ListingRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
        {
          headerName: 'Verified',
          field: 'last_verified_at',
          width: 110,
          valueFormatter: (p: ValueFormatterParams<ListingRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
      ] as ColDef<ListingRow>[],
    [SkuCell, ChannelCell, StatusCell, UrlCell, PrimaryCell]
  );

  const defaultColDef = useMemo<ColDef<ListingRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">Listings</h1>
            {totalCount > 0 && (
              <span className="text-xs text-ink-muted">
                {totalCount} {totalCount === 1 ? 'listing' : 'listings'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <ExportButton gridApiRef={gridApiRef} fileName="arbi-listings" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Status filter tabs */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                aria-pressed={statusFilter === tab.key}
                className={clsx(
                  'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                  statusFilter === tab.key
                    ? 'bg-accent/15 text-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-border mx-1 flex-shrink-0" />

          {/* Channel filter tabs */}
          <div className="flex items-center bg-surface-2 rounded-md p-0.5 gap-0.5 border border-border-subtle">
            {channelTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setChannelFilter(tab.key)}
                aria-pressed={channelFilter === tab.key}
                className={clsx(
                  'px-2 py-1 rounded text-2xs font-medium whitespace-nowrap transition-colors cursor-pointer',
                  channelFilter === tab.key
                    ? 'bg-accent/15 text-accent'
                    : 'text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative w-60 ml-auto">
            <Search
              size={12}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted"
            />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search SKU, title, or URL..."
              aria-label="Search listings by SKU, title, or URL"
              className="arbi-input pl-8 pr-3 py-1 text-xs w-full"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 p-2" style={{ minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowModelType="infinite"
          cacheBlockSize={100}
          maxBlocksInCache={50}
          selectionColumnDef={SELECTION_COLUMN_DEF}
          rowSelection={{
            mode: 'multiRow',
            enableClickSelection: false,
            checkboxes: true,
            headerCheckbox: false,
          }}
          onGridReady={onGridReady}
          animateRows={false}
          headerHeight={36}
          rowHeight={42}
          suppressCellFocus={true}
          suppressHorizontalScroll={true}
          noRowsOverlayComponent={() => (
            <div className="flex flex-col items-center justify-center h-full">
              <Link size={48} className="text-ink-muted mb-3" />
              <p className="text-sm text-ink-muted">
                {statusFilter !== 'all' || channelFilter !== 'all' || searchText
                  ? 'No listings match your filters'
                  : 'No listings yet \u2014 add your first channel listing to start tracking'}
              </p>
            </div>
          )}
        />
      </div>
    </div>
  );
}
